import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import VideoPlayer from '@/components/VideoPlayer';
import VideoFileIcon from '@/pages/Web/PageComponents/MultimodalLearning/icons/VideoFileIcon';
import CirclePlayIcon from '@/pages/Web/PageComponents/MultimodalLearning/icons/CirclePlayIcon';
import AgentLogo from '../../PageComponents/AgentLogo';
import { handleMultimodalDocument, handleMultimodalDocumentToken } from '@/api/grt/ai';
import ToastUtils from '@/utils/ToastUtils';
import { themeMap } from '@/utils/Page/constant';
import s from './style/video.module.scss';

/**
 * 视频播放页面
 * 独立的路由页面，用于播放视频
 */
const VideoPage = () => {
    const { videoId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [videoData, setVideoData] = useState(null);
    const [showVideoList, setShowVideoList] = useState(false);
    const [videoList, setVideoList] = useState([]);
    const [loading, setLoading] = useState(false);
    const playerRef = useRef(null);

    const parsedQuery = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const pageTitle = useMemo(
        () =>
            parsedQuery.get('agentName') ||
            parsedQuery.get('agent_name') ||
            parsedQuery.get('title') ||
            '督学促学智能体',
        [parsedQuery]
    );

    useEffect(() => {
        // 只使用 query 参数构造播放数据，避免依赖 state
        const videoUrl = parsedQuery.get('videoUrl');
        console.log('videoUrl', videoUrl);
        if (videoUrl) {
            setVideoData({
                id: parsedQuery.get('id') || videoId,
                title: parsedQuery.get('title') || '视频播放',
                description: parsedQuery.get('description') || '',
                thumbnail: parsedQuery.get('poster') || '',
                videoUrl,
                duration: parsedQuery.get('duration') || ''
            });
        } else {
            setVideoData(null);
        }

        const videoListKey = parsedQuery.get('videoListKey');
        const videoListParam = parsedQuery.get('videoList');
        
        let resolvedVideoList = [];

        // 优先从 sessionStorage 读取
        if (videoListKey && typeof window !== 'undefined' && window.sessionStorage) {
            try {
                const cachedList = window.sessionStorage.getItem(videoListKey);
                if (cachedList) {
                    const parsedList = JSON.parse(cachedList);
                    if (Array.isArray(parsedList) && parsedList.length > 0) {
                        resolvedVideoList = parsedList;
                        // 读取成功后清理
                        window.sessionStorage.removeItem(videoListKey);
                    }
                } else {
                    console.warn('⚠️ sessionStorage 中没有找到对应的数据，将尝试从 URL 参数读取');
                }
            } catch (err) {
                console.error('❌ 读取缓存视频列表失败:', err);
            }
        }

        // 如果 sessionStorage 没有数据，从 URL 参数读取（备用方案）
        if (!resolvedVideoList.length && videoListParam) {
            try {
                const parsedList = JSON.parse(videoListParam);
                resolvedVideoList = Array.isArray(parsedList) ? parsedList : [];
            } catch (err) {
                console.error('❌ 解析视频列表失败:', err);
            }
        }

        if (!resolvedVideoList.length) {
            console.warn('⚠️ 没有找到视频列表参数，列表将为空');
        }
        setVideoList(resolvedVideoList);
    }, [videoId, parsedQuery]);

    // 保证主题变量生效（独立页面可能未设置 data-theme）
    const resolvedTheme = useMemo(() => {
        const themeColorParam = parsedQuery.get('themeColor');
        const themeNameParam = parsedQuery.get('theme');
        return (
            themeMap?.[themeColorParam] ||
            themeNameParam ||
            document.body.getAttribute('data-theme') ||
            'blue'
        );
    }, [parsedQuery]);

    const logoTheme = useMemo(() => {
        // 如果 themeColor 传的是数字字符串，直接用
        const themeColorParam = parsedQuery.get('themeColor');
        if (themeColorParam && themeMap[themeColorParam]) {
            return Number(themeColorParam);
        }
        // 否则通过名称反查
        const entry = Object.entries(themeMap).find(([, name]) => name === resolvedTheme);
        return entry ? Number(entry[0]) : 1; // 默认蓝色
    }, [parsedQuery, resolvedTheme]);

    useEffect(() => {
        document.body.setAttribute('data-theme', resolvedTheme);
    }, [resolvedTheme]);

    const handleBack = () => {
        navigate(-1);
    };

    const handleShowVideoList = () => {
        setShowVideoList(!showVideoList);
    };

    const handleVideoSelect = useCallback(async (video) => {
        // 如果选择的是当前视频，直接关闭列表（兼容缺少 id 的情况）
        const currentKey = videoData?.id || videoData?.videoUrl || videoId;
        const targetKey = video?.id || video?.resource_id || video?.videoUrl;
        if (currentKey && targetKey && String(targetKey) === String(currentKey)) {
            setShowVideoList(false);
            return;
        }

        setShowVideoList(false);
        setLoading(true);

        try {
            const resourceId = video?.id;
            if (!resourceId) {
                ToastUtils.showToast('缺少资源ID，无法切换视频');
                console.warn('切换视频时缺少资源ID:', video);
                setLoading(false);
                return;
            }

            // 1️⃣ 调用多模态视频详情接口，获取播放基础信息
            const docRes = await handleMultimodalDocument({ id: resourceId });

            if (!docRes?.success || !docRes.data) {
                ToastUtils.showToast(docRes?.msg || '获取视频信息失败');
                setLoading(false);
                return;
            }

            const docData = docRes.data;

            // 优先使用后端直接返回的播放地址
            let playUrl =
                docData.play_url ||
                docData.video_url ||
                docData.url ||
                docData.playUrl ||
                docData.videoUrl ||
                docData.default_url ||
                docData.defaultUrl ||
                '';

            // 取 vod_id / 资产ID，用于获取播放 token
            const vodId = docData.vod_id;
            const assetId = docData.asset_id || docData.assetId || docData.vod_asset_id || vodId;

            // 2️⃣ 如果有 vodId / 资产ID，则再调用 token 接口，拼接 token
            if (assetId) {
                try {
                    const tokenRes = await handleMultimodalDocumentToken(
                        vodId ? { vod_id: vodId } : { asset_id: assetId }
                    );

                    if (tokenRes) {
                        playUrl = `${playUrl}?token=${tokenRes}`;
                    }
                } catch (tokenError) {
                    console.error('获取视频 token 失败:', tokenError);
                    // token 获取失败时，仍然尝试使用基础播放地址
                }
            }

            if (!playUrl) {
                ToastUtils.showToast('未获取到可用的视频播放地址');
                setLoading(false);
                return;
            }

            // 3️⃣ 更新视频数据
            const newVideoData = {
                id: resourceId,
                title: video?.title || docData?.name || '视频播放',
                description: video?.description || docData?.description || '',
                thumbnail: video?.thumbnail || video?.image_url || docData?.extra_obs_id || docData?.poster || '',
                videoUrl: playUrl,
                duration: video?.duration || docData?.duration || ''
            };

            setVideoData(newVideoData);
        } catch (error) {
            console.error('切换视频失败:', error);
            ToastUtils.showToast(error?.message || '切换视频失败，请稍后重试');
        } finally {
            setLoading(false);
        }
    }, [videoData?.id, videoData?.videoUrl, videoId]);

    // 播放结束后自动顺序循环播放下一个
    const handlePlayEnded = useCallback(() => {
        if (!Array.isArray(videoList) || videoList.length === 0) return;
        const currentId = videoData?.id || videoId;
        const currentIndex = videoList.findIndex(item => String(item.id) === String(currentId));
        const nextIndex = currentIndex >= 0 ? (currentIndex + 1) % videoList.length : 0;
        const nextVideo = videoList[nextIndex];
        if (nextVideo) {
            handleVideoSelect(nextVideo);
        }
    }, [videoList, videoData?.id, videoId, handleVideoSelect]);

    const handleCloseDropdown = () => {
        setShowVideoList(false);
    };

    // 处理长文本：中间省略号，末尾内容可见
    const truncateText = (text, maxLength = 18) => {
        if (!text || text.length <= maxLength) {
            return text;
        }
        // 计算前后各显示多少字符
        // 前面显示约40%，后面显示约40%，中间用省略号
        // 确保末尾内容能显示出来（至少显示3-4个字符）
        const frontLength = Math.max(5, Math.floor(maxLength * 0.4)); // 前面至少5个字符
        const backLength = Math.max(4, Math.floor(maxLength * 0.4)); // 后面至少4个字符，确保末尾可见
        const front = text.substring(0, frontLength);
        const back = text.substring(text.length - backLength);
        return `${front}...${back}`;
    };

    if (!videoData) {
        return (
            <div className={s['video-page']}>
                <div className={s['loading']}>加载中...</div>
            </div>
        );
    }

    return (
        <div className={s['video-page']}>
            {/* 顶部导航栏 */}
            <div className={s['video-header']}>
                <div className={s['header-left']}>
                    <AgentLogo theme={logoTheme} style={{ width: 24, height: 24 }} />
                    <h1 className={s['video-title']}>{pageTitle}</h1>
                </div>
                <button 
                    className={`${s['video-list-btn']} ${showVideoList ? s['active'] : ''}`} 
                    onClick={handleShowVideoList} 
                    title="视频列表"
                >
                    <i className="iconfont icon-liebiao"></i>
                    <span>视频列表</span>
                </button>
            </div>

            {/* 视频列表下拉框 */}
            {showVideoList && (
                <div className={s['video-list-dropdown']}>
                    <div className={s['dropdown-header']}>
                        <h3>视频列表</h3>
                        <button className={s['close-btn']} onClick={handleCloseDropdown}>
                            <i className="iconfont icon-guanbi"></i>
                        </button>
                    </div>
                    <div className={s['dropdown-list']}>
                        {videoList.map((video) => {
                            // 匹配当前播放的视频：比较 id（可能是字符串或数字）
                            const currentVideoId = videoData?.id || videoId;
                            const isCurrentVideo = String(video.id) === String(currentVideoId);
                            return (
                                <div 
                                    key={video.id}
                                    className={`${s['video-item']} ${isCurrentVideo ? s['active'] : ''}`}
                                    onClick={() => handleVideoSelect(video)}
                                >
                                    <VideoFileIcon 
                                        width="20" 
                                        height="20" 
                                        color="#4A8DFF"
                                        className={s['video-icon']}
                                    />
                                    <div className={s['video-info']}>
                                        <p className={s['video-title']} title={video.title}>
                                            {truncateText(video.title, 20)}
                                        </p>
                                        <span className={s['video-duration']}>{video.duration}</span>
                                    </div>
                                    {isCurrentVideo && (
                                        <CirclePlayIcon 
                                            width="14px" 
                                            height="14px" 
                                            color="#3B70FF"
                                            className={s['play-icon']}
                                        />
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* 视频播放器区域 */}
            <div className={s['video-content']}>
                <h2 className={s['video-name']}>{videoData.title || '视频标题'}</h2>
                {loading ? (
                    <div style={{ height: '690px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#000', borderRadius: '8px', color: '#fff' }}>
                        切换视频中...
                    </div>
                ) : (
                    <VideoPlayer 
                        key={videoData.id} // 添加 key 确保视频切换时重新渲染
                        videoUrl={videoData.videoUrl || ''}
                        poster={videoData.thumbnail}
                        title={videoData.title}
                        onEnded={handlePlayEnded}
                        autoPlay
                        height="690px"
                    />
                )}
            </div>
        </div>
    );
};

export default VideoPage;

